/**
 * RESPONSIBILITY: the full events list, and the filters over it.
 *
 * The heading, the tags and the events are all on the Events section, so the pencil
 * sitting on this band opens exactly what the band is showing — the list, its tags and
 * the words above it. Content and the way to edit it are the same place.
 *
 * The filters run here, in the browser, over a list that is already loaded: instant, no
 * request per click, and correct because the whole list is present.
 */
import { idOf } from '@framework/core';
import { useMemo, useState } from 'react';
import type { EventsSection, EventRow } from '@app/sections';
import { EventRowItem } from './EventRowItem';
import { nextAt } from '../commons/when';
import { useWords } from '../commons/words';
import { fill } from '../commons/fill';

export function EventsAll({ c, events }: { c: EventsSection; events: EventRow[] }) {
  const w = useWords();

  /**
   * Several tags at once, not one. An event carries several — a VBS week is Children AND
   * Seasonal — so a filter that held one could never express "children's things and the
   * seasonal ones", which is the question somebody scanning this page actually has.
   */
  const [picked, setPicked] = useState<string[]>([]);
  const [term, setTerm] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const tags = c.categories ?? [];

  /**
   * A plain click means "show me this one", because that is what somebody clicking a tag
   * almost always wants — and making it the common case cost nothing, since combining is
   * still there behind a modifier. Toggling every chip meant isolating one tag first
   * required unticking every other, which is work in service of the rarer intent.
   *
   * Clicking the tag that is already alone clears it, so the same chip both enters and
   * leaves a filter and nothing becomes a dead end.
   */
  const pick = (k: string, add: boolean) =>
    setPicked((p) => {
      if (add) return p.includes(k) ? p.filter((x) => x !== k) : [...p, k];
      return p.length === 1 && p[0] === k ? [] : [k];
    });

  const shown = useMemo(() => events.filter((e) => {
    // ANY of the picked tags, not all of them: picking two more tags should widen what
    // you see, which is what somebody ticking a second box expects.
    if (picked.length && !(e.categories ?? []).some((k) => picked.includes(k))) return false;

    if (term.trim()) {
      const hay = `${e.title} ${e.summary ?? ''} ${e.location ?? ''}`.toLowerCase();
      if (!hay.includes(term.trim().toLowerCase())) return false;
    }

    // A date range only excludes events that HAVE a date — "dates vary" is not a reason
    // to hide something from someone searching next month.
    const at = nextAt(e);
    if (at) {
      if (from && at < new Date(from)) return false;
      if (to && at > new Date(`${to}T23:59`)) return false;
    }
    return true;
  }), [events, picked, term, from, to]);

  return (
    <section className="band band-tint">
      <div className="wrap">
        <div className="band-head"><h2>{c.allHeading}</h2></div>

        {/* aria-pressed, because these are switches and not links — without it the only
            thing saying a tag is on is its colour. */}
        <div className="filters" role="group" aria-label={w.filterLabel}>
          <button
            type="button" className={picked.length === 0 ? 'chip chip-on' : 'chip'}
            aria-pressed={picked.length === 0}
            onClick={() => setPicked([])}
          >{w.allLabel}</button>
          {tags.map((k) => (
            <button
              key={k} type="button"
              className={picked.includes(k) ? 'chip chip-on' : 'chip'}
              aria-pressed={picked.includes(k)}
              // metaKey as well as ctrlKey, or this is unreachable on a Mac. A keyboard
              // user gets it too: Ctrl+Enter on a focused button carries the flag.
              onClick={(ev) => pick(k, ev.ctrlKey || ev.metaKey)}
            >{k}</button>
          ))}
        </div>

        {/* Only once there is something to combine WITH. A hint about a gesture that
            cannot do anything yet is noise on the first look at the page. */}
        {tags.length > 1 && (
          <p className="filter-hint">{c.combineHint}</p>
        )}

        <div className="searchrow">
          <input
            type="search" placeholder={c.searchPlaceholder} aria-label={c.searchPlaceholder}
            value={term} onChange={(e) => setTerm(e.target.value)}
          />
          {/* <details> is open-or-closed, which is exactly what this is. */}
          <details className="daterange">
            <summary title={c.datesLabel} aria-label={c.datesLabel}>▦</summary>
            <div className="datepop">
              <label><span>{w.fromLabel}</span>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label><span>{w.toLabel}</span>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </label>
              <button type="button" className="btn btn-ghost" onClick={() => { setFrom(''); setTo(''); }}>
                {w.clearLabel}
              </button>
            </div>
          </details>
        </div>

        <p className="count">
          {fill(w.countTemplate, { shown: shown.length, total: events.length })}
        </p>

        <div className="evlist">
          {shown.map((e) => (
            <EventRowItem key={idOf(e)} e={e} detailsLabel={c.detailsLabel} />
          ))}
          {shown.length === 0 && (
            <p className="lede">{w.noMatchLabel}</p>
          )}
        </div>
      </div>
    </section>
  );
}
